import { CloseCircleOutlined } from '@ant-design/icons';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  DEFAULT_LANGUAGE_TYPE,
  EWithdrawalAdminFailure,
  EWithdrawalChannel,
  EWithdrawalField,
  type IAdminWithdrawalOrderCandidate,
  type ILanguageType,
  type IOrderProduct,
  type IWithdrawalItemInput,
  WITHDRAWAL_EMAIL_MAX_LENGTH,
  WITHDRAWAL_NAME_MAX_LENGTH,
  WITHDRAWAL_ORDER_NUMBER_MAX_LENGTH,
  WITHDRAWAL_TEXT_MAX_LENGTH,
} from '@mahawi/eshop-common/src/types';
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import TableComponent from '../components/table';
import { WITHDRAWAL_CHANNEL_LABEL } from '../components/withdrawal/labels';
import { EDateFormat } from '../fragments/date-format';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { orderLoad } from '../reducers/order/reducer';
import {
  withdrawalCreate,
  withdrawalCreateReset,
  withdrawalOrderSearch,
} from '../reducers/withdrawal/reducer';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

const STAFF_CHANNELS: EWithdrawalChannel[] = [
  EWithdrawalChannel.EMAIL,
  EWithdrawalChannel.LETTER,
  EWithdrawalChannel.PHONE,
  EWithdrawalChannel.OTHER,
];

const CHANNEL_OPTIONS = STAFF_CHANNELS.map((channel: EWithdrawalChannel) => ({
  value: channel,
  label: WITHDRAWAL_CHANNEL_LABEL[channel],
}));

interface INewWithdrawalValues {
  channel: EWithdrawalChannel;
  receivedAt: Dayjs;
  name: string;
  orderNumber: string;
  orderEmail: string;
  confirmationEmail: string;
  reason?: string;
  itemsDescription?: string;
  languageCode: string;
  itemCounts?: Record<string, number | null>;
}

type TCandidateRow = IAdminWithdrawalOrderCandidate & { key: string };

const FORM_FIELD_BY_WITHDRAWAL_FIELD: Record<
  EWithdrawalField,
  keyof INewWithdrawalValues
> = {
  [EWithdrawalField.NAME]: 'name',
  [EWithdrawalField.ORDER_NUMBER]: 'orderNumber',
  [EWithdrawalField.ORDER_EMAIL]: 'orderEmail',
  [EWithdrawalField.CONFIRMATION_EMAIL]: 'confirmationEmail',
  [EWithdrawalField.REASON]: 'reason',
  [EWithdrawalField.ITEMS_DESCRIPTION]: 'itemsDescription',
  [EWithdrawalField.ITEMS]: 'itemCounts',
  [EWithdrawalField.CHANNEL]: 'channel',
  [EWithdrawalField.RECEIVED_AT]: 'receivedAt',
};

const optionalText = (value: string | undefined): string | null =>
  value?.trim() ? value.trim() : null;

const toItems = (
  itemCounts: Record<string, number | null> | undefined,
): IWithdrawalItemInput[] =>
  Object.entries(itemCounts ?? {})
    .filter(([, count]) => typeof count === 'number' && count > 0)
    .map(([productUuid, count]) => ({ productUuid, count: count ?? 0 }));

function WithdrawalNewContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm<INewWithdrawalValues>();
  const [selectedOrder, setSelectedOrder] =
    useState<IAdminWithdrawalOrderCandidate | null>(null);

  const inProcess = useSelector((s: RootState) => s.Withdrawal.inProcess);
  const createdUuid = useSelector((s: RootState) => s.Withdrawal.createdUuid);
  const failure = useSelector((s: RootState) => s.Withdrawal.failure);
  const searchResults = useSelector(
    (s: RootState) => s.Withdrawal.orderSearchResults,
  );
  const loadedOrder = useSelector((s: RootState) => s.Order.order);
  const languages = useSelector((s: RootState) => s.Language.languages);
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const selectedOrderDetail =
    selectedOrder && loadedOrder?.uuid === selectedOrder.uuid
      ? loadedOrder
      : null;

  useEffect((): void => {
    dispatch(withdrawalCreateReset());
  }, [dispatch]);

  useEffect((): void => {
    if (createdUuid) {
      dispatch(withdrawalCreateReset());
      void navigate(`/orders/withdrawal/${createdUuid}`);
    }
  }, [createdUuid, dispatch, navigate]);

  useEffect((): void => {
    if (failure?.reason !== EWithdrawalAdminFailure.INVALID) {
      return;
    }

    form.setFields(
      failure.fields.map((field: EWithdrawalField) => ({
        name: FORM_FIELD_BY_WITHDRAWAL_FIELD[field],
        errors: ['This value is not valid.'],
      })),
    );
  }, [failure, form]);

  useEffect((): void => {
    if (!selectedOrder || !selectedOrderDetail) {
      return;
    }

    const { billingAddress, lead } = selectedOrderDetail;
    const confirmationEmail: string | undefined =
      form.getFieldValue('confirmationEmail');

    form.setFieldsValue({
      name: `${billingAddress.name} ${billingAddress.surname}`.trim(),
      orderNumber: selectedOrder.shortCode,
      orderEmail: billingAddress.email,
      confirmationEmail: confirmationEmail || billingAddress.email,
      languageCode: lead.languageType.code,
      itemCounts: {},
    });
  }, [selectedOrder, selectedOrderDetail, form]);

  const candidateColumns: ColumnsType<TCandidateRow> = [
    { title: 'Order', dataIndex: 'shortCode' },
    { title: 'Billing name', dataIndex: 'billingName' },
    { title: 'Billing email', dataIndex: 'billingEmail' },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (createdAt: string): string =>
        dayjs(createdAt).format(EDateFormat.DATE),
    },
    {
      title: '',
      render: (_value: string, record: TCandidateRow): React.ReactElement => (
        <Button
          size="small"
          type="primary"
          onClick={(): void => {
            setSelectedOrder(record);
            dispatch(orderLoad(record.uuid));
          }}
        >
          Use this order
        </Button>
      ),
    },
  ];

  return (
    <>
      <DetailPageHeader
        title="New withdrawal"
        info={
          <Text type="secondary">
            Record a withdrawal the customer sent by email, letter or phone. The
            customer receives the same confirmation email as for the online
            form.
          </Text>
        }
      />
      <Divider titlePlacement="left">Order (optional)</Divider>
      {selectedOrder ? (
        <Space size={SPACING.sm} style={{ marginBottom: SPACING.lg }}>
          <Text>Order</Text>
          <Link
            to={`/order/${selectedOrder.uuid}`}
            label={selectedOrder.shortCode}
            openInNewTab
          />
          <Button
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={(): void => {
              setSelectedOrder(null);
              form.setFieldValue('itemCounts', {});
            }}
          >
            Clear
          </Button>
        </Space>
      ) : (
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Search orders by short code, UUID or billing email"
            enterButton="Search"
            allowClear
            onSearch={(query: string): void => {
              if (query.trim()) {
                dispatch(withdrawalOrderSearch(query.trim()));
              }
            }}
          />
          {searchResults && (
            <TableComponent
              columns={
                candidateColumns as Parameters<
                  typeof TableComponent
                >[0]['columns']
              }
              dataSource={searchResults.map(
                (candidate: IAdminWithdrawalOrderCandidate) => ({
                  key: candidate.uuid,
                  ...candidate,
                }),
              )}
              isLoading={inProcess}
              emptyText="No orders found"
            />
          )}
        </Space>
      )}

      <Divider titlePlacement="left">Withdrawal</Divider>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          channel: EWithdrawalChannel.EMAIL,
          receivedAt: dayjs(),
          languageCode: DEFAULT_LANGUAGE_TYPE.code,
        }}
        onFinish={(values: INewWithdrawalValues): void => {
          dispatch(
            withdrawalCreate({
              channel: values.channel,
              receivedAt: values.receivedAt.toISOString(),
              name: values.name.trim(),
              orderNumber: values.orderNumber.trim(),
              orderEmail: values.orderEmail.trim(),
              confirmationEmail: values.confirmationEmail.trim(),
              reason: optionalText(values.reason),
              itemsDescription: optionalText(values.itemsDescription),
              orderUuid: selectedOrder?.uuid ?? null,
              items: selectedOrder ? toItems(values.itemCounts) : [],
              languageCode: values.languageCode,
            }),
          );
        }}
      >
        <Space size={SPACING.lg} align="start" wrap>
          <Form.Item
            name="channel"
            label="Channel"
            rules={[{ required: true }]}
            style={{ minWidth: '12rem' }}
          >
            <Select options={CHANNEL_OPTIONS} disabled={inProcess} />
          </Form.Item>
          <Form.Item
            name="receivedAt"
            label="Received at"
            rules={[{ required: true }]}
          >
            <DatePicker
              showTime
              disabled={inProcess}
              disabledDate={(date: Dayjs): boolean =>
                date.isAfter(dayjs(), 'day')
              }
            />
          </Form.Item>
          <Form.Item
            name="languageCode"
            label="Language"
            rules={[{ required: true }]}
            style={{ minWidth: '12rem' }}
          >
            <Select
              showSearch
              disabled={inProcess}
              options={(languages ?? []).map((language: ILanguageType) => ({
                value: language.code,
                label: `${language.code} – ${language.name}`,
              }))}
            />
          </Form.Item>
        </Space>
        <Form.Item
          name="name"
          label="Name"
          rules={[
            { required: true, whitespace: true },
            { max: WITHDRAWAL_NAME_MAX_LENGTH },
          ]}
        >
          <Input disabled={inProcess} />
        </Form.Item>
        <Form.Item
          name="orderNumber"
          label="Order number"
          rules={[
            { required: true, whitespace: true },
            { max: WITHDRAWAL_ORDER_NUMBER_MAX_LENGTH },
          ]}
        >
          <Input disabled={inProcess} />
        </Form.Item>
        <Form.Item
          name="orderEmail"
          label="Order email"
          rules={[
            { required: true, type: 'email' },
            { max: WITHDRAWAL_EMAIL_MAX_LENGTH },
          ]}
        >
          <Input disabled={inProcess} />
        </Form.Item>
        <Form.Item
          name="confirmationEmail"
          label="Confirmation email"
          rules={[
            { required: true, type: 'email' },
            { max: WITHDRAWAL_EMAIL_MAX_LENGTH },
          ]}
        >
          <Input disabled={inProcess} />
        </Form.Item>
        <Form.Item
          name="reason"
          label="Reason (optional)"
          rules={[{ max: WITHDRAWAL_TEXT_MAX_LENGTH }]}
        >
          <Input.TextArea rows={3} disabled={inProcess} />
        </Form.Item>
        <Form.Item
          name="itemsDescription"
          label="Items description (optional)"
          rules={[{ max: WITHDRAWAL_TEXT_MAX_LENGTH }]}
        >
          <Input.TextArea rows={3} disabled={inProcess} />
        </Form.Item>
        {selectedOrderDetail && selectedOrderDetail.products.length > 0 && (
          <>
            <Text strong>Withdrawn items (leave 0 for the whole order)</Text>
            {selectedOrderDetail.products.map((product: IOrderProduct) => (
              <Form.Item
                key={product.uuid}
                name={['itemCounts', product.uuid]}
                label={`${getTranslation(product.names, languageType) ?? product.uuid} (ordered ${product.count})`}
              >
                <InputNumber
                  min={0}
                  max={product.count}
                  precision={0}
                  disabled={inProcess}
                />
              </Form.Item>
            ))}
          </>
        )}
        <Button
          type="primary"
          size="large"
          htmlType="submit"
          disabled={inProcess}
        >
          Record withdrawal
        </Button>
      </Form>
    </>
  );
}

export default WithdrawalNewContainer;
